import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { ItemChangesComponent } from '@modules/team-item-changes/item-changes.component';
import { TASK_DISPOSITION_INLINE_HOST, TaskDispositionDialogService, type TaskDispositionTarget } from '@modules/tasks/services/task-disposition-dialog.service';
import { ChangeDetectorRef } from '@angular/core';
import { TASK_WAITING_INLINE_HOST, TaskWaitingDialogService } from '@modules/tasks/services/task-waiting-dialog.service';
import { taskStateRefusal } from './task-waiting.error';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { ItemReviewComponent } from '@modules/common/components/item-review/item-review.component';
import { ITEM_REVIEW_INLINE_HOST, type ItemReviewTarget } from '@modules/common/components/item-review/item-review-dialog.service';
import { UILocations } from '@modules/common/types/uiLocations';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { ItemQuickCommentComponent } from '@modules/comments/components/item-quick-comment/item-quick-comment.component';
import { ItemWorkspaceChipComponent } from '@modules/common/components/item-workspace/item-workspace-chip.component';
import { ItemWorkspaceAccessComponent } from '@modules/common/components/item-workspace/item-workspace-access.component';
import { ItemTeamLabelComponent } from '@modules/common/components/item-workspace/item-team-label.component';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { canEditDocumentsIn } from '@modules/files/version-history/file-edit-authority';
import { TaskVersionHistoryComponent } from '@modules/tasks/version-history/task-version-history.component';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { HttpContext } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import type { TaskSummaryDto } from '@sharedlib/dtos/dto/tasking-app/task.dto';
import { TASK_HANDOVER_INLINE_HOST } from '@modules/tasks/services/task-handover-dialog.service';
import { TaskHandoverComponent } from './task-handover.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  taskingDraftAudience,
  taskingDraftSuggestions,
  taskingWorkspaceIdentity,
} from '@modules/tasks/services/tasking-draft-audience';
import {
  MAILBOX_TENANT_ID,
  OWNING_SPACE_ID,
  owningTenantContext,
} from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { headerLocationBadge } from '@modules/common/utils/header-location-badge';
import { Application } from '@modules/common/types/application';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { ProjectSummariesCache } from '@modules/tasks/services/project-summaries.cache';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { Section } from '@modules/common/types/section';
import { Tab } from '@modules/common/types/tab';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { CalendarEventsService } from '@modules/form-controls/services/calendar-events.service';
import { TimeZoneSeedingController } from '@modules/form-controls/services/timezone-seeding.controller';
import { AccountTimeZoneSettingsService } from '@modules/account/services/account-timezone-settings.service';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import type { DropdownSelectItem } from '@frontendlib/common/types/dropdown-select-item';
import { priorities } from '@modules/form-controls/types/priorities';
import { SpaceParticipant } from '@modules/settings/types/space-participant';
import { ColumnsService } from '@modules/tasks/services/columns.service';
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { SectionsService } from '@modules/tasks/services/sections.service';
import { TaskingAppSettingsService } from '@modules/tasks/services/settings.service';
import { TaskListStateService } from '@modules/tasks/services/task-list-state.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { TasksTenantScopeService } from '@modules/tasks/services/tasks-tenant-scope.service';
import { type WorkspaceIdentity, WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { Column } from '@modules/tasks/types/column';
import { Project } from '@modules/tasks/types/project';
import { TitleByTasksFilterPipe } from '@modules/tasks/pipes/title-by-tasks-filter.pipe';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { APP_CRUMBS, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type PendingSubtask, Task, type TaskFormGroupConfig } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { isOverdue, mergeDateAndTime } from '@sharedlib/common/utils/date';
import { SignalStitchEventType, SignalType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.less'],
  standalone: false,
  providers: [
    { provide: TASK_DISPOSITION_INLINE_HOST, useFactory: () => {
      const form = inject(TaskFormComponent);
      return {open: (target: TaskDispositionTarget): boolean => form.openInlineDisposition(target)};
    } },
    { provide: TASK_WAITING_INLINE_HOST, useFactory: () => {
      const form = inject(TaskFormComponent);
      return {open: (task: Task): boolean => form.openInlineWaiting(task)};
    } },
    {
      provide: ITEM_REVIEW_INLINE_HOST,
      useFactory: () => {
        const form = inject(TaskFormComponent);
        return { open: (target: ItemReviewTarget): boolean => form.openInlineReview(target) };
      },
    },
    {
      provide: TASK_HANDOVER_INLINE_HOST,
      useFactory: () => {
        const form = inject(TaskFormComponent);
        return { open: (task: TaskSummaryDto): boolean => form.openInlineAssignment(task) };
      },
    },
    { provide: CalendarEventsService, useClass: TasksService },
    TaskListStateService.providers(),
    WorkspaceIdentityService,
  ],
})
export class TaskFormComponent
  extends FullFormBaseComponent<StitchType.TASK, Task, TaskFormGroupConfig>
  implements OnInit
{
  /** Which project the open item is filed under; see {@link headerLocationBadge}. */
  protected readonly locationBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.projectId,
    cache: inject(ProjectSummariesCache),
    search: inject(AdvancedSearchService),
    application: Application.tasking,
    badgeType: 'project',
    alsoWhen: this.openedFromHub,
  });

  public readonly task = input(new Task());
  public readonly insideModal = input(false);
  protected readonly StitchType = StitchType;
  public readonly reviewApi = inject(TasksService).reviewApi;
  private readonly reviewControl = viewChild(ItemReviewComponent);
  protected readonly reviewContentVersion = itemChangeRevision(this.itemUpdated.pipe(map(() => undefined)));
  protected readonly reviewTarget = signal<{ parentId: string; itemId: string; tenantId: string } | null>(null);
  protected readonly reviewItemId = computed(() => {
    const target = this.reviewTarget();
    return target && target.parentId === this.task().id ? target.itemId : this.task().id;
  });
  protected readonly reviewTenantId = computed(() =>
    this.accountKind.isTeam() && this.task().id && this.task().accountId === this.workspaceIdentity()?.spaceTenantId
      ? this.task().accountId
      : undefined,
  );
  public openInlineReview(target: ItemReviewTarget): boolean {
    if (
      !this.accountKind.isTeam() ||
      target.stitchType !== StitchType.TASK ||
      !this.insideModal() ||
      !this.reviewControl() ||
      !target.itemId ||
      target.tenantId !== this.reviewTenantId()
    )
      return false;
    this.reviewTarget.set({ parentId: this.task().id, itemId: target.itemId, tenantId: target.tenantId });
    afterNextRender(() => this.reviewControl()?.openRequest(), { injector: this.assignmentInjector });
    return true;
  }

  private readonly dispositionDialog = inject(TaskDispositionDialogService);
  protected readonly dispositionTarget = signal<{parentId: string; target: TaskDispositionTarget} | null>(null);
  public openInlineDisposition(target: TaskDispositionTarget): boolean {
    if (!this.insideModal() || target.task.accountId !== this.reviewTenantId()) return false;
    this.dispositionTarget.set({parentId:this.task().id,target});
    return true;
  }
  public override archive(): void {
    if (this.shouldAskDisposition('archive')) {
      this.askDisposition('archive', () => super.archive());
      return;
    }
    super.archive();
  }
  public override delete(): void {
    if (this.shouldAskDisposition('trash')) {
      this.askDisposition('trash', () => super.delete());
      return;
    }
    super.delete();
  }
  private shouldAskDisposition(action: 'archive' | 'trash'): boolean {
    return !!this.reviewTenantId() && !this.stitchItem.completed &&
      (action === 'archive' ? !this.form.controls.archived.value : !this.stitchItem.deleted) &&
      (!!this.handoverView()?.pending() || this.reviewControl()?.review()?.state === 'requested');
  }
  private askDisposition(action: 'archive' | 'trash', proceed: () => void): void {
    this.dispositionDialog.open({task:new Task({...this.stitchItem}),action,proceed},this.assignmentInjector);
  }

  private readonly waitingDialog = inject(TaskWaitingDialogService);
  private readonly waitingChangeDetector = inject(ChangeDetectorRef);
  private readonly waitingSpaces = inject(SpacesService);
  private readonly waitingVersion = signal(0);
  private readonly waitingSpace = toSignal(toObservable(computed(() => {
    this.waitingVersion();
    return this.reviewTenantId() && this.task().waiting ? this.task().accountId : null;
  })).pipe(switchMap(tenant => tenant ? this.waitingSpaces.getCurrentSpace() : of(null))));
  protected readonly waitingTarget = signal<{ parentId: string; task: Task } | null>(null);
  protected readonly waitingActionError = signal<string | null>(null);
  protected readonly waitingDetails = computed(() => {
    this.waitingVersion();
    const task = this.task();
    if (!this.accountKind.isTeam() || !this.reviewTenantId() || !task.waiting) return null;
    const space = this.waitingSpace();
    const member = space?.id === task.accountId ? space.participants.find(p => p.id === task.waiting?.by) : undefined;
    return { ...task.waiting, name: member?.fullName?.trim() || member?.userName || 'Former member' };
  });
  public openInlineWaiting(task: Task): boolean {
    if (!this.accountKind.isTeam() || !this.insideModal() || !task.id || task.accountId !== this.reviewTenantId())
      return false;
    this.waitingTarget.set({ parentId: this.task().id, task });
    return true;
  }
  protected openWaiting(): void {
    if (!this.accountKind.isTeam() || !this.reviewTenantId()) return;
    this.waitingDialog.open(this.task(), this.assignmentInjector, item => this.waitingSaved(item));
  }
  protected waitingSaved(item: Task): void {
    if (item.accountId !== this.reviewTenantId() || item.id !== this.task().id) return;
    const target = this.task();
    this.tasksService.getItem(item.id, {requestContext: owningTenantContext(item.accountId)}).pipe(take(1), takeUntil(this.alive)).subscribe(task => {
      if (this.task() !== target) return;
      target.waiting = task.waiting;
      this.stitchItem.waiting = task.waiting;
      this.waitingVersion.update(version => version + 1);
      this.waitingChangeDetector.markForCheck();
    });
  }
  protected stopWaiting(): void {
    if (!this.accountKind.isTeam() || !this.reviewTenantId()) return;
    const target = this.task();
    this.waitingActionError.set(null);
    this.tasksService.clearWaiting(target.id, target.accountId).pipe(takeUntil(this.alive)).subscribe({
      next: ({item}) => this.waitingSaved(new Task(item)),
      error: (error: unknown) => {
        if (this.task() !== target) return;
        this.waitingActionError.set(taskStateRefusal(error, 'Could not clear the waiting state. Try again.'));
      },
    });
  }

  protected readonly assignmentTarget = signal<{ parentId: string; task: TaskSummaryDto } | null>(null);
  protected readonly handoverTask = computed(() => {
    const target = this.assignmentTarget();
    return target && target.parentId === this.task().id ? target.task : this.task();
  });
  private readonly handoverView = viewChild<TaskHandoverComponent>('handover');
  /** A hand-over is a team feature: a personal login gets none of its controls, whichever space is open. */
  protected readonly accountKind = inject(AccountKindService);
  private readonly assignmentInjector = inject(Injector);

  public openInlineAssignment(task: TaskSummaryDto): boolean {
    if (!this.insideModal() || !task.id || task.accountId !== this.workspaceIdentity()?.spaceTenantId) return false;
    this.assignmentTarget.set({ parentId: this.task().id, task });
    afterNextRender(() => this.handoverView()?.openAssign(), { injector: this.assignmentInjector });
    return true;
  }

  public changesKey = 'task';
  public tabs: Tab[] = [
    { title: 'Task', value: 'task' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'tasks';
  public suggestions: AutocompleteFactory<Project>;
  public columnsSuggestions: AutocompleteFactory<Column>;
  public sectionsSuggestions: AutocompleteFactory<Section>;
  public assigneeSuggestions: AutocompleteFactory<SpaceParticipant>;
  public triggerRefreshSubtasks = new Subject<void>();
  protected pendingSubtasks: PendingSubtask[] = [];
  public showIssueKey = false;
  public tasksFilters: TasksFilters;
  public readonly priorityOptions: DropdownSelectItem<string>[] = priorities.map((p) => ({
    title: p.title,
    value: p.value,
    icon: p.icon,
  }));
  // Computed signal for selected priority icon
  public readonly selectedPriorityIcon = computed<Icon | undefined>(
    () => this.priorityOptions.find((p) => p.value === this.priorityControlValue())?.icon as Icon | undefined,
  );
  public readonly priorityControlValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const priorityControl = form.get('priority');
        return priorityControl ? priorityControl.valueChanges.pipe(startWith(priorityControl.value)) : of(null);
      }),
      distinctUntilChanged(),
    ),
  );

  private readonly completed = signal<boolean>(false);

  private readonly toDateValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const toDateControl = form.get('toDate');
        return toDateControl ? toDateControl.valueChanges.pipe(startWith(toDateControl.value)) : of(null);
      }),
    ),
  );

  private readonly toTimeValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const toTimeControl = form.get('toTime');
        return toTimeControl ? toTimeControl.valueChanges.pipe(startWith(toTimeControl.value)) : of(null);
      }),
    ),
  );

  private readonly allDayValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const allDayControl = form.get('allDay');
        return allDayControl ? allDayControl.valueChanges.pipe(startWith(allDayControl.value)) : of(false);
      }),
    ),
  );

  protected readonly isOverdue = computed<boolean>(() => {
    // The controls are already display-frame carriers (seeded in the account zone by
    // `seedTaskControlsForZone`), so merging their parts yields the due moment in that same frame — which
    // is the frame `isOverdue` compares in, given the zone below (SBUX-829).
    const floating = this.allDayValue() || !this.toTimeValue();
    return isOverdue({
      dueAt: mergeDateAndTime(this.toDateValue(), floating ? undefined : this.toTimeValue()),
      floating,
      completed: this.completed(),
      timeZone: this.displayTimeZoneService.timeZone(),
    });
  });

  protected dragDataType = DragDataTypes.task;

  private readonly taskListStateService: TaskListStateService = inject(TaskListStateService, { self: true });
  private readonly signalsService = inject(SignalsService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = toObservable(this.displayTimeZoneService.timeZone);
  // #916: seeds a new item's zone from the person's own zone — the one the grid is drawn in.
  private readonly accountTimeZoneSettingsService = inject(AccountTimeZoneSettingsService);
  protected readonly tzSeeder = new TimeZoneSeedingController();
  private readonly tasksTenantScopeService = inject(TasksTenantScopeService, { optional: true });
  private readonly workspaceIdentityService = inject(WorkspaceIdentityService);
  protected readonly workspaceIdentity = signal<WorkspaceIdentity | undefined>(undefined);
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    this.workspaceIdentityService,
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  protected readonly UILocations = UILocations;
  protected readonly workspaceRevision = itemChangeRevision(this.stitchItemChanged);
  protected readonly workspaceComponents = {
    'app-item-changes': ItemChangesComponent,
    'app-item-quick-comment': ItemQuickCommentComponent,
    'app-item-workspace-chip': ItemWorkspaceChipComponent,
    'app-item-workspace-access': ItemWorkspaceAccessComponent,
    'app-item-team-label': ItemTeamLabelComponent,
    'app-item-workspace-footer': ItemWorkspaceFooterComponent,
    'app-task-version-history': TaskVersionHistoryComponent,
  };

  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem);
  }
  private readonly workspaceProjectCache = inject(ProjectSummariesCache);
  private readonly parentForFooter = computed(() => {
    this.workspaceRevision();
    const task = this.stitchItem;
    return this.insideTeam && !task.projectId && task.parentId
      ? { id: task.parentId, accountId: task.accountId }
      : undefined;
  });
  private readonly footerParent = toSignal(
    toObservable(this.parentForFooter).pipe(
      distinctUntilChanged((previous, current) => previous?.id === current?.id && previous?.accountId === current?.accountId),
      switchMap((parent) =>
        parent
          ? this.tasksService.getItem(parent).pipe(
              take(1),
              catchError(() => of(undefined)),
            )
          : of(undefined),
      ),
    ),
  );
  private readonly footerProject = this.workspaceProjectCache.getForItem(
    computed(() => {
      this.workspaceRevision();
      if (!this.insideTeam) return undefined;
      const task = this.stitchItem;
      const parent = this.footerParent();
      return {
        id:
          task.projectId ??
          (parent?.id === task.parentId && parent?.accountId === task.accountId ? parent?.projectId : undefined),
        accountId: task.accountId,
      };
    }),
  );

  /** The project this task sits in, named by the header's project badge. */
  protected readonly headerProject = this.workspaceProjectCache.getForItem(
    computed(() => {
      this.workspaceRevision();
      const task = this.stitchItem;
      return task?.projectId ? { id: task.projectId, accountId: task.accountId } : undefined;
    }),
  );

  private readonly titleByTasksFilter = new TitleByTasksFilterPipe(inject(Injector));
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(TaskingAppStateService, {
    appLabel: APP_CRUMBS.tasks,
    viewTitle: (folder) => this.titleByTasksFilter.transform(folder, null, true),
  });

  /** [workspace ›] Tasking app › the list's view › this task. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      container: this.headerProject()?.title,
      fallbackView: 'Tasks',
      itemTitle: this.task().title || 'New Task',
      itemId: this.task()?.id,
    });
  }

  protected get workspaceLabelInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.stitchItem };
  }

  protected readonly openWorkspaceAccess = (): void => {
    this.selectedTab = 'access';
  };

  protected get workspaceChipInputs(): Record<string, unknown> {
    return { ...this.workspaceLabelInputs, openAccess: this.openWorkspaceAccess, compact: this.contracted };
  }

  protected get itemChangesInputs(): Record<string, unknown> | undefined {
    const item = this.stitchItem;
    if (
      !item?.id || !item.accountId ||
      this.spaceContextStore.type() !== SpaceType.SHARED_SPACE ||
      item.accountId !== this.spaceContextStore.contextId()
    ) return undefined;
    return { path: '/api/tasking/tasks', itemId: item.id, tenantId: item.accountId };
  }

  protected get workspaceTabs(): Tab[] {
    const tabs = this.tabsWithCounts().flatMap((tab) =>
      tab.value === 'comments' && this.itemChangesInputs
        ? [tab, { title: 'Activity', value: 'activity' }]
        : [tab],
    );
    const versioned = this.historyAvailable() ? [...tabs, { title: 'Version history', value: 'versions' }] : tabs;
    return this.insideTeam && this.selectedTab === 'access' ? [...versioned, { title: 'Access', value: 'access' }] : versioned;
  }

  /** A team's own saved task inside that team (#1948, TW-COLLAB-11), as Activity is offered. */
  protected historyAvailable(): boolean {
    return !!this.itemChangesInputs;
  }

  protected get versionHistoryInputs(): Record<string, unknown> {
    return {
      task: this.task(),
      // The role's Edit Documents rule in the task's own workspace (#2069's rule; the file history reads it the same way).
      canEdit: canEditDocumentsIn(this.permissionsService, this.task().accountId),
      held: this.saveInProgress || !this.pristine,
      restored: this.applyRestoredTask,
    };
  }

  protected readonly openVersionHistory = (): void => {
    if (this.historyAvailable()) this.selectedTab = 'versions';
  };

  /** A restore answer is the task's current content (its completion as answered); the form shows it at once. */
  private readonly applyRestoredTask = (answer: Partial<Task>): void => {
    const fresh = new Task(answer);
    const task = this.task();
    if (fresh.id !== task.id || fresh.accountId !== task.accountId) return;
    const pristine = this.pristine;
    Object.assign(task, fresh);
    this.completed.set(!!task.completed);
    if (pristine) this.stitchItemChanged.next();
    this.itemUpdated.emit(task);
    this.waitingChangeDetector.markForCheck();
  };

  protected readonly openItemComments = (): void => {
    this.selectedTab = 'comments';
  };

  protected get quickCommentInputs(): Record<string, unknown> {
    return {
      ...this.workspaceLabelInputs,
      noun: 'task',
      selectedTab: this.selectedTab,
      openComments: this.openItemComments,
    };
  }

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.stitchItem,
      noun: 'Tasks',
      collection: this.footerProject()?.title,
      archived: this.form?.controls.archived.value ?? this.stitchItem.archived,
      deleted: this.form?.controls.deleted.value ?? this.stitchItem.deleted,
    };
  }

  protected readonly sharingTenantId = signal<string | null>(null);
  protected readonly settledTenant = toSignal(this.tasksTenantScopeService?.selectedTeamId() ?? of(null), {
    initialValue: undefined,
  });
  private readonly cancelPendingAudience = new Subject<void>();
  private awaitingDraftAudience = false;
  private creatingDraft: Task | undefined;
  protected readonly audienceResolved = computed(
    () => !!this.stitchItem?.accountId || !!this.requestContext || this.settledTenant() !== undefined,
  );
  protected readonly uploadTenantItems = computed(() => [
    { accountId: this.stitchItem?.accountId ?? this.sharingTenantId() ?? this.settledTenant() ?? undefined },
  ]);
  protected readonly itemTenantContext = computed<HttpContext | undefined>(
    () => owningTenantContext(this.task().accountId ?? this.sharingTenantId()) ?? this.requestContext,
  );
  protected readonly saveLabel = computed(() => {
    const identity = this.workspaceIdentity();
    if (!identity) {
      return 'Save';
    }
    return this.sharingTenantId() === identity.spaceTenantId ? 'Save to team workspace' : 'Save to my workspace';
  });

  constructor(
    private tasksService: TasksService,
    private projectsService: ProjectsService,
    private columnsService: ColumnsService,
    private sectionsService: SectionsService,
    injector: Injector,
    private settingsService: TaskingAppSettingsService,
  ) {
    super(injector, tasksService);
    this.suggestions = taskingDraftSuggestions(
      () => this.draftAudience(),
      (context) => this.projectsService.getAutocompleteSuggestions({}, () => context),
    );
    this.assigneeSuggestions = taskingDraftSuggestions(
      () => this.draftAudience(),
      () => this.tasksService.getAssigneeAutocompleteSuggestions(),
    );
    this.sharingTenantId.set(this.tasksTenantScopeService?.tenantId() ?? null);
    taskingWorkspaceIdentity(
      this.workspaceIdentityService,
      this.spaceContextStore,
      this.tasksTenantScopeService,
      () => {
        if (this.awaitingDraftAudience) {
          this.cancelPendingAudience.next();
          this.saveInProgress = false;
        }
        this.workspaceIdentity.set(undefined);
        this.sharingTenantId.set(this.stitchItem?.accountId ?? this.tasksTenantScopeService?.tenantId() ?? null);
      },
    )
      .pipe(takeUntilDestroyed())
      .subscribe((identity) => {
        this.workspaceIdentity.set(identity);
        if (!this.stitchItem?.id && identity) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ?? this.tasksTenantScopeService?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      });
  }

  /**
   * Lifecycle
   */

  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes.task && this.creatingDraft && this.stitchItem !== this.creatingDraft) {
      this.cancelPendingAudience.next();
      this.saveInProgress = false;
      this.creatingDraft = undefined;
      this.requestContext = this.stitchItem?.accountId ? owningTenantContext(this.stitchItem.accountId) : undefined;
      this.sharingTenantId.set(this.stitchItem?.accountId ?? this.settledTenant() ?? null);
    }
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'comments') this.openItemComments();
    if (changes.tab && this.tab === 'access' && this.insideTeam) this.openWorkspaceAccess();
    if (changes.tab && this.tab === 'versions') this.openVersionHistory();
  }

  private draftAudience(): Observable<HttpContext> {
    return taskingDraftAudience(
      this.stitchItem,
      this.requestContext,
      () => this.sharingTenantId(),
      this.tasksTenantScopeService,
    );
  }

  public override handleCancel(): void {
    if (this.awaitingDraftAudience) {
      this.cancelPendingAudience.next();
      this.saveInProgress = false;
    }
    super.handleCancel();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initTimeZoneSeeding();

    this.signalsService.getSignal(SignalType.STITCH_EVENT).pipe(
      filter(event => event.stitchType === StitchType.TASK && event.eventType === SignalStitchEventType.SNOOZED &&
        event.spaceId === this.task().accountId && event.ids.includes(this.task().id)),
      takeUntil(this.alive),
    ).subscribe(() => this.close.emit(true));

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => this.completed.set(this.stitchItem.completed));
    this.itemUpdated.pipe(takeUntil(this.alive)).subscribe(() => {
      this.completed.set(this.stitchItem.completed);
      // The remote re-read mutates the same task object, so its Waiting band needs a new revision.
      this.waitingVersion.update(version => version + 1);
      this.waitingChangeDetector.markForCheck();
    });
    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      // A new task that arrives with a tenant has its destination fixed by its source (#1447 R3b: chat).
      if (this.stitchItem?.accountId) {
        this.sharingTenantId.set(this.stitchItem.accountId);
        this.requestContext = owningTenantContext(this.stitchItem.accountId);
      }
    });

    this.stitchItemChanged
      .pipe(
        switchMap(() =>
          this.form.controls.projectId.valueChanges.pipe(startWith(this.form.controls.projectId.value as string)),
        ),
        takeUntil(this.alive),
      )
      .subscribe((projectId) => {
        this.columnsSuggestions = this.columnsService.getAutocompleteSuggestions({ projectId }, () =>
          this.itemTenantContext(),
        );
        this.sectionsSuggestions = this.sectionsService.getAutocompleteSuggestions({ containersIds: [projectId] }, () =>
          this.itemTenantContext(),
        );

        // Clear column and section when project changes
        if (projectId !== this.stitchItem?.projectId) {
          this.form.controls.columnId.setValue(null);
          this.form.controls.sectionId.setValue(null);
        }
      });

    this.stitchItemChanged
      .pipe(
        switchMap(() =>
          this.signalsService
            .getSignal(SignalType.RELOAD_PROJECT_BOARD, this.spaceContextStore.contextId())
            .pipe(
              filter(
                ({ containerId }) =>
                  !!this.form.controls.projectId.value && containerId === this.form.controls.projectId.value,
              ),
            ),
        ),
        takeUntil(this.alive),
      )
      .subscribe(() => {
        const projectId = this.form.controls.projectId.value as string;
        this.columnsSuggestions = this.columnsService.getAutocompleteSuggestions({ projectId }, () =>
          this.itemTenantContext(),
        );
        this.sectionsSuggestions = this.sectionsService.getAutocompleteSuggestions({ containersIds: [projectId] }, () =>
          this.itemTenantContext(),
        );
      });

    this.stitchItemChanged
      .pipe(
        switchMap(() => this.form.controls.columnId.valueChanges),
        filter((columnId) => columnId === null),
        filter(() => !!this.form.controls.projectId.value),
        switchMap(() =>
          this.columnsService
            .search({ projectId: this.form.controls.projectId.value }, { requestContext: this.itemTenantContext() })
            .pipe(
              map((response) => response.items.find((column) => column.uncategorized)),
              take(1),
            ),
        ),
        filter((column): column is Column => !!column),
        takeUntil(this.alive),
      )
      .subscribe((uncategorizedColumn) => {
        this.form.controls.columnId.setValue(uncategorizedColumn.id, { emitEvent: false });
      });

    this.stitchItemChanged
      .pipe(
        switchMap(() => combineLatest([this.taskListStateService.value(), this.displayTimeZone$])),
        takeUntil(this.alive),
      )
      .subscribe(([taskListState, displayTimeZone]) => {
        this.tasksFilters = new TasksFilters({ parentsIds: [this.stitchItem.id], displayTimeZone });
        this.tasksFilters.applyListState(taskListState);
      });

    this.settingsService
      .listAll()
      .pipe(takeUntil(this.alive))
      .subscribe(({ issueKeyEnabled }) => (this.showIssueKey = issueKeyEnabled));

    this.stitchItemChanged
      .pipe(
        switchMap(() => this.form.controls.allDay.valueChanges.pipe(startWith(this.form.controls.allDay.value))),
        takeUntil(this.alive),
      )
      .subscribe((allDay) => {
        const fromDateControl = this.form.controls.fromDate;
        if (allDay) {
          fromDateControl.setValidators(Validators.required);
        } else {
          fromDateControl.removeValidators(Validators.required);
        }
        fromDateControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  dndDrop(dragData: DragData) {
    if (!this.stitchItem?.id) {
      return;
    }

    this.stitchService.linkDragData(this.stitchItem, dragData);
  }

  handleComplete(): void {
    this.stitchItem.completed = !this.stitchItem.completed;
    this.completed.set(this.stitchItem.completed);
    this.tasksService.update(this.stitchItem);
  }

  protected override performCreate(stitchItem: Task): Observable<Task> {
    const subtasks = this.pendingSubtasks.length > 0 ? this.pendingSubtasks : null;
    this.creatingDraft = this.stitchItem;
    this.awaitingDraftAudience = true;
    return taskingDraftAudience(
      this.stitchItem,
      this.requestContext,
      () => this.sharingTenantId(),
      this.tasksTenantScopeService,
    ).pipe(
      tap((context) => {
        this.awaitingDraftAudience = false;
        this.requestContext = context;
        const owner = context.get(OWNING_SPACE_ID) ?? context.get(MAILBOX_TENANT_ID);
        if (owner) {
          this.stitchItem.accountId = owner;
          stitchItem.accountId = owner;
          this.sharingTenantId.set(owner);
        }
      }),
      switchMap(() => this.tasksService.create(stitchItem, { requestContext: this.requestContext }, subtasks)),
      takeUntil(this.cancelPendingAudience),
      takeUntil(this.alive),
      finalize(() => {
        this.awaitingDraftAudience = false;
        this.saveInProgress = false;
      }),
      tap(() => {
        if (this.uploadsInProgress() === 0) {
          this.form.controls.uploads.reset([]);
        }
        this.pendingSubtasks = [];
        this.triggerRefreshSubtasks.next();
      }),
    );
  }

  protected override requestConfig(): StitchRequestConfig {
    const requestContext = this.itemTenantContext();
    if (!requestContext) {
      return super.requestConfig();
    }
    return { requestContext };
  }

  /**
   * Helpers
   */

  get stitchItem(): Task {
    return this.task();
  }

  protected shouldRefreshList(prev, current) {
    return Task.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Task {
    return Task.fromFormGroup(this.form);
  }

  /**
   * A new task seeds its timezone from the account/Settings timezone, falling back to the browser
   * then UTC. Never overrides a hand-picked value and never touches existing tasks; the picker
   * shows where the value came from. Tasks have no calendar rung.
   */
  private initTimeZoneSeeding(): void {
    this.tzSeeder.start({
      control: this.form.controls.timeZone,
      destroyRef: this.destroyRef,
      isExistingEntity: !!this.stitchItem?.id,
      settingsTimeZone$: this.accountTimeZoneSettingsService.timeZoneOnce(),
      rungs: ['settings', 'browser', 'utc'],
    });
  }

  protected asFormGroup() {
    return this.task().asFormGroup({ maxAttachmentSize: this.maxUploadSize() });
  }
}
